import { useState } from "react"

import { InfoIcon } from "@minuthq/meatball-ui-icons"
import { colors, RadioButton } from "@minuthq/meatball-ui-react"
import { Badge } from "@minuthq/meatball-ui-react"

import { ParadiseDateFilter } from "src/components/Paradise/ParadiseDateFilter"
import { ParadisePager } from "src/components/Paradise/ParadisePager"
import { ParadiseDeleteQuoteDialog } from "src/components/Paradise/ParadiseQuotes/ParadiseDeleteQuoteDialog"
import { ParadiseQuotesFilters } from "src/components/Paradise/ParadiseQuotes/ParadiseQuotesFilters"
import { ParadiseQuoteStatus } from "src/components/Paradise/ParadiseQuotes/ParadiseQuoteStatus"
import { TParadiseCombinedQuoteWithUsers } from "src/components/Paradise/ParadiseQuotes/paradiseQuoteTypes"
import { isQuoteExpired } from "src/components/Paradise/ParadiseQuotes/paradiseQuoteUtils"
import {
  TParadiseQuotesFilters,
  TSortedParadiseQuotesFilters,
} from "src/components/Paradise/ParadiseQuotes/useParadiseQuotesFilter"
import { ParadiseSelectUsersByEmail } from "src/components/Paradise/ParadiseSelectUsersByEmail"
import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import {
  IdWrapper,
  RadioWrapper,
  StatusWrapper,
  TopWrapper,
} from "src/components/Paradise/sharedStyles"
import { Routes } from "src/router/routes"
import { TSetSearchParamsProp } from "src/router/useSearchParams"
import { DropdownMultiSelect } from "src/ui/DropdownSelect/DropdownMultiSelect"
import { MoreButton } from "src/ui/GridTable/MoreButton"
import { ColumnPopoverWrapper } from "src/ui/GridTable/useTableColumns/tableColumnStyles"
import {
  TTableSort,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import FilterIcon from "src/ui/icons/calibrating.svg"
import { InternalLink } from "src/ui/Link/InternalLink"
import { MTooltip } from "src/ui/MTooltip/MTooltip"
import { formatUtcDate } from "src/utils/l10n"
import { Maybe } from "src/utils/tsUtil"

export function ParadiseQuotesTable({
  quotesWithUsers,
  filter,
  setFilter,
  sortedFilters,
  sort,
  setSort,
  limit,
  offset,
  setOffset,
  totalCount,
  isLoading,
  isLoadingMore,
  error,
}: {
  quotesWithUsers: TParadiseCombinedQuoteWithUsers[]
  filter: TParadiseQuotesFilters
  setFilter: TSetSearchParamsProp<TParadiseQuotesFilters>
  sortedFilters: TSortedParadiseQuotesFilters
  sort?: Maybe<TTableSort>
  setSort?: (sort: TTableSort) => void
  limit: number
  offset: number
  setOffset: (offset: number) => void
  totalCount: number
  isLoading: boolean
  isLoadingMore: boolean
  error?: {
    hasError: boolean
    title: React.ReactNode
    description?: React.ReactNode
  }
}) {
  const [deleteQuoteId, setDeleteQuoteId] = useState<string | null>(null)

  const {
    headerElements,
    rows,
    templateColumns,
    interactiveColumns,
    interactiveVisibleColumns,
    updateColumnVisibility,
  } = useTableColumns<TParadiseCombinedQuoteWithUsers>({
    columns: [
      {
        value: "id",
        label: "Id",
        renderCell: (data) => (
          <div>
            <IdWrapper>
              <InternalLink to={Routes.ParadiseQuote.location(data.id)}>
                {data.id}
              </InternalLink>
              {!!data.deleted_at && (
                <MTooltip title="Deleted">
                  <InfoIcon width={12} fill={colors.systemError} />
                </MTooltip>
              )}
            </IdWrapper>
          </div>
        ),
        columnWidth: "min-content",
      },
      {
        value: "customer_name",
        label: "Customer name",
        renderCell: (data) => <div>{data.customer_name}</div>,
        columnWidth: "min-content",
      },
      {
        value: "sales_rep",
        label: "Sales rep email",
        renderCell: (data) => (
          <div>
            {!!data.salesRep ? (
              <div>
                <InternalLink
                  to={Routes.ParadiseUser.location(data.salesRep.user_id)}
                >
                  {data.salesRep.email}
                </InternalLink>
              </div>
            ) : (
              <div>{data.sales_rep_user_id}</div>
            )}
          </div>
        ),
        columnWidth: "min-content",
        popoverContent: (
          <ColumnPopoverWrapper>
            <ParadiseSelectUsersByEmail
              selectedUserIds={filter.quotes_sales_rep_user_ids || []}
              onChange={(userIds) => {
                setFilter("quotes_sales_rep_user_ids", userIds)
              }}
            />
          </ColumnPopoverWrapper>
        ),
        popoverIcon: FilterIcon,
      },
      {
        value: "status",
        label: "Status",
        renderCell: (data) => (
          <div>
            <div>
              <ParadiseQuoteStatus status={data.status} />
            </div>
          </div>
        ),
        columnWidth: "min-content",
        popoverContent: (
          <ColumnPopoverWrapper>
            <RadioWrapper>
              <RadioButton
                onSelected={() => {
                  setFilter("quotes_status", "new")
                }}
                label="New"
                name="status"
                value="new"
                selected={filter.quotes_status === "new"}
              />
              <RadioButton
                onSelected={() => {
                  setFilter("quotes_status", "opened")
                }}
                label="Opened"
                name="status"
                value="opened"
                selected={filter.quotes_status === "opened"}
              />
              <RadioButton
                onSelected={() => {
                  setFilter("quotes_status", "purchased")
                }}
                label="Purchased"
                name="status"
                value="purchased"
                selected={filter.quotes_status === "purchased"}
              />
            </RadioWrapper>
          </ColumnPopoverWrapper>
        ),
        popoverIcon: FilterIcon,
        enableSort: true,
      },
      {
        value: "created_at",
        label: "Created at",
        renderCell: (data) => (
          <div>
            {formatUtcDate({
              date: data.created_at,
            })}
          </div>
        ),
        columnWidth: "min-content",
        enableSort: true,
      },
      {
        value: "expires_at",
        label: "Expires at",
        renderCell: (data) => (
          <div>
            <StatusWrapper>
              <div>
                {formatUtcDate({
                  date: data.expires_at,
                })}
              </div>
              {isQuoteExpired(data) && (
                <Badge color="error" size="x-small">
                  Expired
                </Badge>
              )}
            </StatusWrapper>
          </div>
        ),
        columnWidth: "min-content",
        popoverContent: (
          <ColumnPopoverWrapper $minWidth={300}>
            <ParadiseDateFilter
              selectedDates={{
                after: filter.quotes_expires_after
                  ? new Date(filter.quotes_expires_after)
                  : undefined,
                before: filter.quotes_expires_before
                  ? new Date(filter.quotes_expires_before)
                  : undefined,
              }}
              onChange={(dates) => {
                setFilter(
                  "quotes_expires_after",
                  dates.after ? dates.after.toISOString() : null
                )
                setFilter(
                  "quotes_expires_before",
                  dates.before ? dates.before.toISOString() : null
                )
              }}
            />
          </ColumnPopoverWrapper>
        ),
        popoverIcon: FilterIcon,
        enableSort: true,
      },
      {
        value: "order_number",
        label: "Order number",
        renderCell: (data) => (
          <div>
            {data.order_number ? (
              <InternalLink
                to={Routes.ParadiseOrder.location(data.order_number)}
              >
                {data.order_number}
              </InternalLink>
            ) : (
              <div>-</div>
            )}
          </div>
        ),
        columnWidth: "min-content",
      },
      {
        value: "customer",
        label: "Customer",
        renderCell: (data) => (
          <div>
            {!!data.customer ? (
              <div>
                <InternalLink
                  to={Routes.ParadiseUser.location(data.customer.user_id)}
                >
                  {data.customer.email}
                </InternalLink>
              </div>
            ) : (
              <div>{data.customer_id || "-"}</div>
            )}
          </div>
        ),
        columnWidth: "1fr",
      },

      {
        value: "actions",
        label: "",
        renderCell: (data) => (
          <div>
            <MoreButton
              deleteLabel="Delete"
              onDelete={() => {
                setDeleteQuoteId(data.id)
              }}
            />
          </div>
        ),
        columnWidth: "auto",
      },
    ],
    data: quotesWithUsers,
    sort,
    onSortChange: setSort,
  })

  return (
    <div>
      <TopWrapper>
        <ParadiseQuotesFilters
          sortedFilters={sortedFilters}
          setFilter={setFilter}
        />
        <DropdownMultiSelect
          label="Columns"
          selectedValues={interactiveVisibleColumns.map((c) => c.value)}
          options={interactiveColumns}
          onChange={({ checked, option }) => {
            updateColumnVisibility(option.value, !checked)
          }}
        />
      </TopWrapper>

      <ParadiseTable
        header={headerElements}
        rows={rows ?? []}
        templateColumns={templateColumns}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        error={error}
      />

      <ParadisePager
        limit={limit}
        offset={offset}
        setOffset={setOffset}
        totalCount={totalCount}
      />

      {deleteQuoteId && (
        <ParadiseDeleteQuoteDialog
          open={!!deleteQuoteId}
          onClose={() => setDeleteQuoteId(null)}
          quoteId={deleteQuoteId}
        />
      )}
    </div>
  )
}
